import { useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SiteNavbar from '@/components/feature/SiteNavbar';
import SiteFooter from '@/components/feature/SiteFooter';
import ScrollToTop from '@/components/feature/ScrollToTop';
import RevealWrapper from '@/pages/home/components/RevealWrapper';
import gSoftLogoDark from '@/pages/images/g-soft-logo-beyaz2.png';
import { getProductProgress } from '@/config/productProgress';
import { useProductProgressMap } from '@/context/ProductProgressContext';
import { PRODUCT_CARD_THEMES, type ProductCardTheme, type ProductThemeKey } from '@/config/productCardTheme';

type GameStatus = 'LIVE' | 'DEV' | 'SOON' | 'CANCELLED';

interface GameItem {
  id: string;
  productKey: ProductThemeKey;
  nameKey: string;
  genreKey: string;
  status: GameStatus;
  version: string;
  descKey: string;
  longDescKey: string;
  image: string;
  externalLink?: string;
  tags: string[];
  stats: { labelKey: string; valueKey: string }[];
  progress: number;
  progressLabelKey: string;
  archiveNotes?: string[];
}

const GAMES: GameItem[] = [
];

const ARCHIVED_GAMES: GameItem[] = [
  {
    id: 'A01',
    productKey: 'anilar-muzesi',
    nameKey: 'games.am.name',
    genreKey: 'games.am.category',
    status: 'CANCELLED',
    version: 'v0.0.0-archived',
    descKey: 'games.am.description',
    longDescKey: 'games.am.longDescription',
    image: gSoftLogoDark,
    tags: [
      'games.am.tag.atmosphere',
      'games.am.tag.storyDriven',
      'games.am.tag.mobile',
      'games.am.tag.archive',
    ],
    stats: [
      { labelKey: 'games.am.stat.status', valueKey: 'games.am.statValue.status' },
      { labelKey: 'games.am.stat.platform', valueKey: 'common.platform.android' },
      { labelKey: 'games.am.stat.version', valueKey: 'common.value.na' },
      { labelKey: 'games.am.stat.release', valueKey: 'common.value.none' },
    ],
    progress: getProductProgress('anilar-muzesi'),
    progressLabelKey: 'games.am.progressLabel',
    archiveNotes: [
      'games.archive.note.0',
      'games.archive.note.1',
      'games.archive.note.2',
      'games.archive.note.3',
    ],
  },
];

function MiniProgressBar({
  progress,
  label,
  cancelled = false,
  tone,
}: {
  progress: number;
  label: string;
  cancelled?: boolean;
  tone: ProductCardTheme;
}) {
  const { t } = useTranslation();
  const [displayed, setDisplayed] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let frame: number;
    const startTime = performance.now();
    const duration = 1200;
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const tt = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - tt, 3);
      setDisplayed(Math.round(eased * progress));
      if (tt < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [started, progress]);

  if (cancelled) {
    return (
      <div ref={ref} className="mt-6">
        <div className="flex items-center justify-between mb-1.5">
          <span className={`font-mono text-[10px] tracking-widest uppercase ${tone.progressLabel}`}>{t('games.devProgress')}</span>
          <div className="flex items-center gap-2">
            <span className={`font-mono text-[10px] tracking-widest uppercase ${tone.progressLabel}`}>{t('games.status.cancelled')}</span>
          </div>
        </div>
        <div className={`relative h-px w-full overflow-hidden ${tone.progressTrack}`}>
          <div className={`absolute left-0 top-0 h-full w-0 ${tone.progressFill}`} />
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="mt-6">
      <div className="flex items-center justify-between mb-1.5">
        <span className={`font-mono text-[10px] tracking-widest uppercase ${tone.progressLabel}`}>{t('games.devProgress')}</span>
        <div className="flex items-center gap-2">
          <span className={`font-mono text-[10px] tracking-widest ${tone.progressLabel}`}>{label}</span>
          <span className={`font-mono text-sm font-black tabular-nums ${tone.progressValue}`}>
            {displayed}<span className={`text-xs ${tone.progressLabel}`}>%</span>
          </span>
        </div>
      </div>
      <div className={`relative h-px w-full overflow-hidden ${tone.progressTrack}`}>
        <div
          className={`absolute left-0 top-0 h-full ${tone.progressFill}`}
          style={{
            width: started ? `${progress}%` : '0%',
            transition: started ? 'width 1.2s cubic-bezier(0.16,1,0.3,1)' : 'none',
          }}
        />
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: GameStatus }) {
  const { t } = useTranslation();
  if (status === 'LIVE') {
    return (
      <span className="font-mono text-xs px-2.5 py-1 border border-white/30 text-white bg-white/5 tracking-widest">
        {t('products.status.live')}
      </span>
    );
  }
  if (status === 'DEV') {
    return (
      <span className="font-mono text-xs px-2.5 py-1 border border-white/10 text-neutral-500 tracking-widest">
        {t('products.status.dev')}
      </span>
    );
  }
  if (status === 'CANCELLED') {
    return (
      <span className="font-mono text-xs px-2.5 py-1 border border-white/5 text-neutral-700 tracking-widest line-through">
        {t('products.status.cancelled')}
      </span>
    );
  }
  return (
    <span className="font-mono text-xs px-2.5 py-1 border border-white/8 text-neutral-600 tracking-widest">
      {t('products.status.soon')}
    </span>
  );
}

export default function GamesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { progress: liveProgress } = useProductProgressMap();

  const GAMES_LINKS = [
    { label: t('nav.home'),    href: '/',      isRoute: true },
    { label: t('nav.apps'),    href: '/apps',  isRoute: true },
    { label: t('nav.games'),   href: '/games', isRoute: true },
    { label: t('nav.contact'), href: '#contact' },
  ];

  return (
    <div className="min-h-screen bg-[#0c0c0c]">
      <SiteNavbar links={GAMES_LINKS} sectionAware={false} />

      {/* Page Header */}
      <section className="pt-32 pb-16 px-6 lg:px-14 border-b border-white/5">
        <RevealWrapper direction="up" threshold={0.1}>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <p className="font-mono text-xs text-neutral-600 tracking-widest uppercase mb-4">{t('games.label')}</p>
              <h1 className="text-5xl lg:text-7xl font-black text-white tracking-tight leading-none mb-4">{t('games.title')}</h1>
              <p className="text-neutral-500 text-sm leading-relaxed max-w-lg">{t('games.description')}</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="font-mono text-3xl font-black text-white">{GAMES.length}</p>
                <p className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{t('games.total')}</p>
              </div>
              <div className="w-px h-12 bg-white/10" />
              <div className="text-right">
                <p className="font-mono text-3xl font-black text-white">{GAMES.filter(g => g.status === 'LIVE').length}</p>
                <p className="font-mono text-xs text-neutral-600 tracking-widest uppercase">{t('games.live')}</p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* Games List */}
      <section>
        {GAMES.map((game, idx) => {
          const theme = PRODUCT_CARD_THEMES[game.productKey];
          return (
          <RevealWrapper key={game.id} direction="up" delay={idx * 100} threshold={0.1}>
            <div className={`border-b ${theme.rowBorder} ${theme.rowHover} transition-colors duration-300 ${idx % 2 === 1 ? theme.rowAlt : ''}`}>
              <div className="px-6 lg:px-14 py-12 lg:py-16">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                  {/* Left: Number + Icon */}
                  <div className="lg:col-span-1 flex lg:flex-col items-center lg:items-start gap-4">
                    <span className="font-black text-4xl text-white/5 leading-none">{game.id}</span>
                    <div className={`w-14 h-14 border flex items-center justify-center ${theme.iconBox}`}>
                      <img src={game.image} alt={t(game.nameKey)} className="w-10 h-10 object-contain" />
                    </div>
                  </div>

                  {/* Center: Info */}
                  <div className="lg:col-span-8">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h2 className="text-2xl lg:text-4xl font-black text-white tracking-tight">{t(game.nameKey)}</h2>
                      <StatusBadge status={game.status} />
                    </div>
                    <p className={`font-mono text-xs tracking-wider mb-5 ${theme.meta}`}>
                      {t(game.genreKey)} · {game.version}
                    </p>
                    <p className="text-neutral-300 text-sm leading-relaxed mb-3">{t(game.descKey)}</p>
                    <p className="text-neutral-600 text-sm leading-relaxed max-w-xl mb-6">{t(game.longDescKey)}</p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-8">
                      {game.tags.map((tagKey) => (
                        <span
                          key={tagKey}
                          className={`font-mono text-xs border px-3 py-1.5 transition-colors duration-200 ${theme.tag}`}
                        >
                          {t(tagKey)}
                        </span>
                      ))}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {game.stats.map((s) => (
                        <div key={s.labelKey} className={`border px-4 py-3 ${theme.statBox}`}>
                          <p className={`font-mono text-base font-black mb-1 ${theme.statValue}`}>{t(s.valueKey)}</p>
                          <p className={`font-mono text-[10px] tracking-widest uppercase ${theme.statLabel}`}>{t(s.labelKey)}</p>
                        </div>
                      ))}
                    </div>

                    <MiniProgressBar progress={liveProgress[game.productKey]} label={t(game.progressLabelKey)} tone={theme} />
                  </div>

                  {/* Right: Status */}
                  <div className="lg:col-span-3 flex flex-col gap-3 lg:items-end lg:justify-start">
                    {game.externalLink ? (
                      <a
                        href={game.externalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/btn inline-flex items-center gap-3 px-6 py-4 border border-white/20 text-white font-mono text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all duration-300 sm:whitespace-nowrap"
                      >
                        {t('games.download')}
                        <i className="ri-arrow-right-line text-base group-hover/btn:translate-x-1 transition-transform duration-200" />
                      </a>
                    ) : (
                      <div className="inline-flex items-center gap-3 px-6 py-4 border border-white/5 text-neutral-700 font-mono text-xs tracking-widest uppercase sm:whitespace-nowrap">
                        <i className="ri-time-line" />
                        {t('games.inDevelopment')}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </RevealWrapper>
          );
        })}
      </section>

      {/* Archive Section */}
      <section className="border-t border-white/5 mt-0">
        <RevealWrapper direction="up" threshold={0.1}>
          <div className="px-6 lg:px-14 pt-16 pb-6">
            <div className="flex items-center gap-4 mb-2">
              <p className="font-mono text-xs text-neutral-700 tracking-widest uppercase">{t('games.archive.label')}</p>
              <div className="flex-1 h-px bg-white/5" />
            </div>
            <p className="font-mono text-xs text-neutral-700 tracking-wider max-w-lg">
              {t('games.archive.description')}
            </p>
          </div>
        </RevealWrapper>

        {ARCHIVED_GAMES.map((game, idx) => {
          const theme = PRODUCT_CARD_THEMES[game.productKey];
          return (
          <RevealWrapper key={game.id} direction="up" delay={idx * 100} threshold={0.1}>
            <div className={`border-b ${theme.rowBorder} ${theme.rowAlt}`}>
              <div className="px-6 lg:px-14 py-10 lg:py-14">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
                  {/* Left: Number + Icon */}
                  <div className="lg:col-span-1 flex lg:flex-col items-center lg:items-start gap-4">
                    <span className="font-black text-4xl text-red-900/20 leading-none">{game.id}</span>
                    <div className={`w-14 h-14 border flex items-center justify-center grayscale ${theme.iconBox}`}>
                      <img src={game.image} alt={t(game.nameKey)} className="w-10 h-10 object-contain" />
                    </div>
                  </div>

                  {/* Center: Info */}
                  <div className="lg:col-span-8">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h2 className="text-2xl lg:text-4xl font-black text-neutral-600 tracking-tight line-through decoration-red-900/50">{t(game.nameKey)}</h2>
                      <StatusBadge status={game.status} />
                    </div>
                    <p className={`font-mono text-xs tracking-wider mb-5 ${theme.meta}`}>
                      {t(game.genreKey)} · {game.version}
                    </p>
                    <p className="text-neutral-600 text-sm leading-relaxed mb-3">{t(game.descKey)}</p>
                    <p className="text-neutral-700 text-sm leading-relaxed max-w-xl mb-6">{t(game.longDescKey)}</p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-8">
                      {game.tags.map((tagKey) => (
                        <span
                          key={tagKey}
                          className={`font-mono text-xs border px-3 py-1.5 ${theme.tag}`}
                        >
                          {t(tagKey)}
                        </span>
                      ))}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {game.stats.map((s) => (
                        <div key={s.labelKey} className={`border px-4 py-3 ${theme.statBox}`}>
                          <p className={`font-mono text-base font-black mb-1 ${theme.statValue}`}>{t(s.valueKey)}</p>
                          <p className={`font-mono text-[10px] tracking-widest uppercase ${theme.statLabel}`}>{t(s.labelKey)}</p>
                        </div>
                      ))}
                    </div>

                    <MiniProgressBar progress={liveProgress[game.productKey]} label={t(game.progressLabelKey)} cancelled tone={theme} />

                    {/* Archive Notes */}
                    {game.archiveNotes && (
                      <div className="mt-6 border border-red-900/15 bg-[#0f0808] px-5 py-4">
                        <p className="font-mono text-[10px] text-red-900/50 tracking-widest uppercase mb-3">{t('games.archive.notesLabel')}</p>
                        <ul className="space-y-1.5">
                          {game.archiveNotes.map((noteKey) => (
                            <li key={noteKey} className="flex items-start gap-2">
                              <span className="font-mono text-red-900/40 text-xs mt-0.5">—</span>
                              <span className="font-mono text-xs text-neutral-700">{t(noteKey)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Right: Cancelled indicator */}
                  <div className="lg:col-span-3 flex flex-col gap-3 lg:items-end lg:justify-start">
                    <button
                      onClick={() => navigate('/anilar-muzesi')}
                      className="group/btn inline-flex items-center gap-3 px-6 py-4 border border-red-900/30 text-red-900/60 font-mono text-xs tracking-widest uppercase hover:border-red-800/50 hover:text-red-700/80 transition-all duration-300 sm:whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-archive-line" />
                      {t('games.archive.record')}
                      <i className="ri-arrow-right-line text-base group-hover/btn:translate-x-1 transition-transform duration-200" />
                    </button>
                    <span className="font-mono text-[10px] text-red-900/30 tracking-widest uppercase px-6">
                      {t('games.archive.notInCatalog')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </RevealWrapper>
          );
        })}
      </section>

      {/* Bottom CTA */}
      <RevealWrapper direction="up" threshold={0.1}>
        <div className="px-6 lg:px-14 py-16 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <p className="font-mono text-xs text-neutral-600 tracking-widest uppercase mb-2">{t('games.exploreMore')}</p>
            <p className="text-neutral-400 text-sm">{t('games.exploreDesc')}</p>
          </div>
          <button
            onClick={() => navigate('/apps')}
            className="group/btn inline-flex items-center gap-3 px-6 py-4 border border-white/20 text-white font-mono text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all duration-300 sm:whitespace-nowrap cursor-pointer"
          >
            {t('games.viewApps')}
            <i className="ri-arrow-right-line text-base group-hover/btn:translate-x-1 transition-transform duration-200" />
          </button>
        </div>
      </RevealWrapper>

      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}
